import type { SWRInfiniteConfiguration } from "swr/infinite";
import type { BareFetcher } from "swr/_internal";
import useSWRInfinite from "swr/infinite";
import { useCallback } from "react";
import { AppError } from "@/models/appError";
import {
  convertContestResponseToIContestListItemOrUndefined,
  type IContestListItem,
} from "@/models/contestListItem";
import { QueryRepository } from "@/repositories/queryRepository";
import { filterUndefined } from "@/utils/filter-undefined";
import { useAuth } from "./useAuth";

const SWR_KEY = "contest-list-key";

type Data = {
  items: IContestListItem[];
  nextPage?: number;
  hasNext: boolean;
};
type SWRError = AppError;

const REQUEST_ID = "fetch-contest-list";
const DEFAULT_LIMIT = 20;

const fetcher = async (
  _key: string,
  pageNumber: number,
  limit: number = DEFAULT_LIMIT,
): Promise<Data> => {
  const queryRepository = new QueryRepository();

  const response = await queryRepository.quickQuery([
    {
      requestId: REQUEST_ID,
      rawQueryRequest: {
        contestPageRequest: {
          requireContest: {
            requireId: { value: true },
            requireStatus: { value: true },
            requireName: { value: true },
            requireDescription: { value: true },
            requireDiscloseAt: { value: true },
            requireOpenEntryAt: { value: true },
            requireCloseEntryAt: { value: true },
            requireAnnounceResultAt: { value: true },
            requireThumbnail: {
              requireServingUrl: { value: true },
            },
          },
          includeOpened: { value: true },
          includeClosed: { value: true },
          includeDisclosed: { value: true },
          includeResultAnnounced: { value: true },
          page: {
            pageNumber: {
              value: pageNumber,
            },
            limit: {
              value: limit,
            },
            requirePageInfo: {
              requireHasNextPage: { value: true },
            },
          },
        },
      },
    },
  ]);

  const contestPage = response.responseList.find(
    (item) => item.requestId?.value === REQUEST_ID,
  )?.contestPage;
  const hasNextPage = contestPage?.page?.hasNextPage?.value ?? false;

  return {
    items:
      contestPage?.contestList
        ?.map(convertContestResponseToIContestListItemOrUndefined)
        ?.filter(filterUndefined) ?? [],
    nextPage: hasNextPage ? pageNumber + 1 : undefined,
    hasNext: hasNextPage,
  };
};

type Params = {
  config?: Partial<SWRInfiniteConfiguration<Data, SWRError, BareFetcher<Data>>>;
};

export const useFetchContentList = ({ config }: Params = {}) => {
  const { tellerUser } = useAuth();

  const getKey = useCallback(
    (_: number, prevData: Data) => {
      if (!tellerUser?.id?.value) {
        return null;
      }

      // 最後に達した
      if (prevData && !prevData.hasNext) return;

      if (!prevData) {
        return [SWR_KEY, 0];
      }
      if (prevData.hasNext && prevData.nextPage) {
        return [SWR_KEY, prevData.nextPage];
      }
      return;
    },
    [tellerUser],
  );

  return useSWRInfinite<Data, SWRError>(
    getKey,
    ([key, pageNumber]: [string, number]) => fetcher(key, pageNumber),
    config,
  );
};
